import { assertExists } from './assert';

/**
 * Map of currently supported currencies.
 */
export const LOCALE_CURRENCY_MAP: Record<string, string> = {
  'en-US': 'USD', // US Dollar
  'en-GB': 'GBP', // British Pound
  'en-CA': 'CAD', // Canadian Dollar
  'en-AU': 'AUD', // Australian Dollar
  'en-IN': 'INR', // Indian Rupee
  'en-ZA': 'ZAR', // South African Rand
  'fr-FR': 'EUR', // Euro (France)
  'de-DE': 'EUR', // Euro (Germany)
  'it-IT': 'EUR', // Euro (Italy)
  'es-ES': 'EUR', // Euro (Spain)
  'es-MX': 'MXN', // Mexican Peso
  'pt-BR': 'BRL', // Brazilian Real
  'ja-JP': 'JPY', // Japanese Yen
  'zh-CN': 'CNY', // Chinese Yuan
  'zh-TW': 'TWD', // Taiwan Dollar
  'ko-KR': 'KRW', // South Korean Won
  'ru-RU': 'RUB', // Russian Ruble
  'tr-TR': 'TRY', // Turkish Lira
  'nl-NL': 'EUR', // Euro (Netherlands)
  'sv-SE': 'SEK', // Swedish Krona
  'pl-PL': 'PLN', // Polish Zloty
  'da-DK': 'DKK', // Danish Krone
  'no-NO': 'NOK', // Norwegian Krone
  'ar-SA': 'SAR', // Saudi Riyal
  'he-IL': 'ILS', // Israeli Shekel
  'th-TH': 'THB', // Thai Baht
  'vi-VN': 'VND', // Vietnamese Dong
};

/**
 * Gets the currency symbol for the provided locale.
 *
 * @param locale Locale.
 * @param currencyCode Optional currency code. It will be retrieved from the locale if not provided.
 * @returns A string containing a currency symbol.
 * @throws {AssertionError} If the locale cannot be found.
 */
export const getLocaleCurrencySymbol = (
  locale: string,
  currencyCode?: string
): string => {
  if (!currencyCode) {
    currencyCode = getLocaleCurrencyCode(locale);
  }

  assertExists(currencyCode);

  const currencyFormatPart = Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
  })
    .formatToParts()
    .find((part) => part.type === 'currency');

  assertExists(currencyFormatPart);
  return currencyFormatPart.value; // This is the currency symbol.
};

/**
 * Gets the currency code for the provided locale.
 *
 * @param locale Locale.
 * @returns The currency code for the locale or undefined if we don't support the locale.
 */
export const getLocaleCurrencyCode = (locale: string) => {
  const value: string | undefined = LOCALE_CURRENCY_MAP[locale];
  return value;
};
